import * as React from 'react'
import { useQuery } from 'react-query'
import { getFeedbackInfo, paramsFeedbackInfo } from '@feedback/services'
import { PhotoConsumer, PhotoProvider } from 'react-photo-view'
import { formType } from '@feedback/pages/allfb/ticketForm'
import styles from '@feedback/pages/allfb/ticketDetail/index.module.scss'
import { getTimeFormat, getTextByKey, getLangKey } from '@feedback/utils/util'
import ModalVideo from '@feedback/components/ModalVideo'
import { getAppConfig } from '@feedback/utils/CONST'
import clsx from 'clsx'

export interface IFeedbackInfo {
  title?: string | undefined
  content?: string | undefined
  created_at?: string | undefined
  feedback_status?: number | undefined
  resolve_time?: string
  cur_score: number
  score: number
  star: number
  images?: {
    path: string
    [key: string]: any
  }[]
  other?: {
    origin_date: {
      type: formType
      data: string
    }
    work_order: {
      type: formType
      data: string
    }
    qusType: {
      type: formType
      data: string
    }
    question_type: {
      type: formType
      data: string
    }
    [key: string]: {
      type: formType
      data: any
    }
  }
}

export const fetchFeedbackInfo = async ({
  feedback_id
}: paramsFeedbackInfo): Promise<IFeedbackInfo> => {
  const { code, data, message } = (await getFeedbackInfo({ feedback_id })) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  return data
}

// 工单信息
const FeedbackInfo: React.FC<{ feedback_id: string }> = ({ feedback_id }) => {
  const { appid } = getAppConfig() || {}
  const langKey = getLangKey()
  const [openURL, setOpenURL] = React.useState('')

  const feedbackInfo = useQuery(['getFeedbackInfo', feedback_id], () =>
    fetchFeedbackInfo({ feedback_id })
  )

  if (feedbackInfo.isLoading || !feedbackInfo?.data?.created_at) {
    return null
  }

  const { content, images, other } = feedbackInfo.data || {}
  const { work_order, qusType, question_type, origin_date, ...rest } =
    other || {}

  return (
    <>
      <section
        className={clsx(styles.info)}
        dir={langKey === 'ar-SA' ? 'rtl' : 'ltr'}
      >
        <div className='time'>
          {getTimeFormat(feedbackInfo?.data?.created_at)}
        </div>

        {work_order && (
          <div className='item'>
            <h2 className='title'>{getTextByKey('feedback id')}:</h2>
            <p className='content'>{work_order?.data || '--'}</p>
          </div>
        )}

        {(qusType?.data || question_type?.data) && (
          <div className='item'>
            <h2 className='title'>{getTextByKey('question type')}:</h2>
            <p className='content'>
              {qusType?.data || ''}
              {qusType?.data && question_type?.data ? ' > ' : ''}
              {question_type?.data || ''}
            </p>
          </div>
        )}

        {content && (
          <div className='item'>
            <h2 className='title'>{getTextByKey('description')}:</h2>
            <p className='content'>{content || '--'}</p>
          </div>
        )}

        {Array.isArray(images) && images.length > 0 && (
          <div className='item'>
            <h2 className='title'>{getTextByKey('images')}:</h2>
            <div className='images'>
              <PhotoProvider>
                {images?.map((image, index) => (
                  <PhotoConsumer key={index} src={image.path as string}>
                    <img src={image.path} alt='' />
                  </PhotoConsumer>
                ))}
              </PhotoProvider>
            </div>
          </div>
        )}

        {rest &&
          Object.keys(rest).map((key) => {
            if (!other?.[key]?.data) return null
            return (
              <React.Fragment key={key}>
                {[formType.string, formType.int, formType.date].includes(
                  other?.[key]?.type as formType
                ) && (
                  <div className='item'>
                    <h2 className='title'>{key}</h2>
                    <p className='content'>
                      {other?.[key]?.type === formType.date
                        ? getTimeFormat(other?.[key]?.data)
                        : other?.[key]?.data}
                    </p>
                  </div>
                )}
                {other?.[key].type === formType.select && (
                  <div className='item'>
                    <h2 className='title'>{key}</h2>
                    <p className='content'>
                      {Array.isArray(other?.[key]?.data)
                        ? other?.[key]?.data.join(', ')
                        : ''}
                    </p>
                  </div>
                )}
                {other?.[key].type === formType.image && (
                  <div className='item'>
                    <h2 className='title'>{key}</h2>
                    <div className='images'>
                      <PhotoProvider>
                        {other?.[key].data?.map(
                          (url: string, index: number) => (
                            <PhotoConsumer key={index} src={url}>
                              <img src={url} alt='' />
                            </PhotoConsumer>
                          )
                        )}
                      </PhotoProvider>
                    </div>
                  </div>
                )}
                {other?.[key].type === formType.video && (
                  <div className='item'>
                    <h2 className='title'>{key}</h2>
                    <div className='videos'>
                      {other?.[key]?.data?.map?.(
                        (url: string, index: number) => (
                          <div
                            className="video-item"
                            key={index}
                            style={{ backgroundImage: `url(${url}?x-oss-process=video/snapshot,t_10000,m_fast,w_100,h_180)` }}
                          >
                            <i
                              className='icon-play'
                              onClick={() => {
                                setOpenURL(url)
                              }}
                            />
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            )
          })}
      </section>

      <ModalVideo
        open={Boolean(openURL)}
        url={openURL}
        onClose={() => setOpenURL('')}
      />
    </>
  )
}

export default FeedbackInfo
